import { NgStyle } from '@angular/common'
import { HttpParams } from '@angular/common/http'
import { ChangeDetectorRef, Component, DestroyRef, ElementRef, OnInit, ViewEncapsulation, inject } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormsModule } from '@angular/forms'
import { FaIconComponent } from '@fortawesome/angular-fontawesome'
import {
    IconDefinition,
    faClipboardList,
    faCreditCard,
    faFileInvoiceDollar,
    faTrowelBricks,
    faTruckPickup,
    faWrench,
    faX,
} from '@fortawesome/free-solid-svg-icons'
import { WjInputModule } from '@grapecity/wijmo.angular2.input'
import imageCompression, { Options } from 'browser-image-compression'

import { ListItem, PurchaseOrder, PurchaseOrderType, PurchaseOrderTypeEx, RepositoryModal } from '../../shared'
import { CreditCardFormComponent } from './form-components/credit-card-form.component'
import { EquipmentFormComponent } from './form-components/equipment-form.component'
import { EquipmentWoFormComponent } from './form-components/equipment-wo-form.component'
import { ExpenseFormComponent } from './form-components/expense-form.component'
import { JobMaterialFormComponent } from './form-components/job-material-form.component'
import { CreditCardItem, EquipmentItem, EquipmentWoItem, ExpenseItem, JobMaterialItem } from './form-components/po-line-item.model'
import { PurchaseOrdersComponent } from './purchase-orders.component'

@Component({
    templateUrl: './purchase-order.component.html',
    styleUrls: ['./purchase-order.component.css'],
    standalone: true,
    imports: [
        FormsModule,
        WjInputModule,
        FaIconComponent,
        NgStyle,
        JobMaterialFormComponent,
        CreditCardFormComponent,
        EquipmentWoFormComponent,
        EquipmentFormComponent,
        ExpenseFormComponent,
    ],
    encapsulation: ViewEncapsulation.None,
})
export class PurchaseOrderComponent extends RepositoryModal<PurchaseOrder> implements OnInit {
    /** Expose for template @switch / @case bind to enum safely. */
    readonly PoType = PurchaseOrderType

    faX = faX
    faTrowelBricks = faTrowelBricks
    faTruckPickup = faTruckPickup
    faFileInvoiceDollar = faFileInvoiceDollar
    faClipboardList = faClipboardList
    faWrench = faWrench

    vendorOptions: ListItem<number>[] = []
    jobOptions: ListItem<string>[] = []
    wizardSelectedJob = ''
    isLoadingVendors = false
    isJobListLoading = false

    images: any[] = []
    progress = 0

    step = -1
    isInitialWizard = false
    isWorkOrder = false

    jobMaterialItem = new JobMaterialItem()
    creditCardItem = new CreditCardItem()
    equipmentWoItem = new EquipmentWoItem()
    equipmentItem = new EquipmentItem()
    expenseItem = new ExpenseItem()

    poExpectedDate: Date | null = new Date()
    poOrderDate: Date | null = new Date()

    purchaseOrderTypes = [
        PurchaseOrderType.JobMaterial,
        PurchaseOrderType.CreditCard,
        PurchaseOrderType.Equipment,
        PurchaseOrderType.GeneralLedger,
    ]

    private readonly typeCardConfig: Record<number, { icon: IconDefinition; cardClass: string; subtitle: string }> = {
        [PurchaseOrderType.JobMaterial]: { icon: faTrowelBricks, cardClass: 'report-card-primary', subtitle: 'Materials charged to a job' },
        [PurchaseOrderType.CreditCard]: { icon: faCreditCard, cardClass: 'report-card-info', subtitle: 'Card purchases' },
        [PurchaseOrderType.Equipment]: { icon: faTruckPickup, cardClass: 'report-card-warning', subtitle: 'Equipment parts and repairs' },
        [PurchaseOrderType.GeneralLedger]: {
            icon: faFileInvoiceDollar,
            cardClass: 'report-card-neutral',
            subtitle: 'General ledger expenses',
        },
    }

    private readonly typeTitles: Record<number, string> = {
        [PurchaseOrderType.JobMaterial]: 'Job Material',
        [PurchaseOrderType.CreditCard]: 'Credit Card',
        [PurchaseOrderType.Equipment]: 'Equipment',
        [PurchaseOrderType.GeneralLedger]: 'General Ledger',
    }

    get wizardTitle(): string {
        if (this.step === 0) {
            return 'Pick a Purchase Order Type'
        }
        if (this.step === 1) {
            return 'Is this tied to a Work Order?'
        }
        if (this.step === 2) {
            const prefix = this.typeTitles[this.target.type] ?? ''
            if (this.target.type === PurchaseOrderType.Equipment) {
                return this.isWorkOrder ? 'Equipment Work Order Purchase Order' : 'Equipment Purchase Order'
            }
            return prefix + ' Purchase Order'
        }
        return (this.typeTitles[this.target.type] ?? '') + ' Purchase Order'
    }

    getCardClass(type: PurchaseOrderType): string {
        return this.typeCardConfig[type]?.cardClass ?? ''
    }

    getCardIcon(type: PurchaseOrderType): IconDefinition {
        return this.typeCardConfig[type]?.icon ?? faTrowelBricks
    }

    getCardSubtitle(type: PurchaseOrderType): string {
        return this.typeCardConfig[type]?.subtitle ?? ''
    }

    private crd = inject(ChangeDetectorRef)
    private destroyRef = inject(DestroyRef)
    private el = inject(ElementRef)
    private parentComponent!: PurchaseOrdersComponent

    getTypeDisplay(type: PurchaseOrderType): string {
        return PurchaseOrderTypeEx.toDisplay(type)
    }

    private updateModalSize() {
        const dialog = this.el.nativeElement.closest('.modal-dialog')
        if (!dialog) return
        dialog.classList.toggle('po-wizard-narrow', this.step >= 0 && this.step < 2)
    }

    ngOnInit() {
        if (this.isDuplicating) {
            this.target['id'] = undefined as any
            this.target.imageUrls = []
            this.target.poNumber = ''
        }
        if (this.isAdding) {
            this.target.createdOn = new Date()
        }

        this.parentComponent = this.editOptions.initializeParams.component as PurchaseOrdersComponent
        this.isInitialWizard = !!this.editOptions.initializeParams.isInitialWizard

        if (this.isAdding && this.target.type === undefined) {
            this.step = 0
        }

        // Silently load job/vendor data in the background for use later in the wizard
        if (this.step === 0) {
            if (this.parentComponent.jobMap.length === 0) {
                this.isJobListLoading = true
            } else {
                this.jobOptions = this.parentComponent.jobMap
            }

            const parentJob = this.parentComponent.selectedJob()
            if (parentJob && parentJob !== '') {
                this.wizardSelectedJob = parentJob
                this.target.job = parentJob

                if (this.parentComponent.approvedVendors && this.parentComponent.approvedVendors.length > 0) {
                    this.vendorOptions = this.parentComponent.approvedVendors.map(x => new ListItem(x.vendorId, x.vendorName))
                } else {
                    this.fetchVendorsForJob(parentJob)
                }
            }
        }

        // When editing, populate vendors from what the parent already has
        if (this.isEditing) {
            this.populateVendorsFromParent()
        }

        // Set the job from the parent's selected job when editing/duplicating
        if (this.isEditing || this.isDuplicating) {
            const selectedJob = this.parentComponent.selectedJob()
            if (selectedJob) {
                this.target.job = selectedJob
            }
        }

        this.updateModalSize()
    }

    private populateVendorsFromParent() {
        // Use job-approved vendors if available, otherwise fall back to all vendors
        if (this.parentComponent.approvedVendors && this.parentComponent.approvedVendors.length > 0) {
            this.vendorOptions = this.parentComponent.approvedVendors.map(x => new ListItem(x.vendorId, x.vendorName))

            // When editing, ensure the current vendor is in the list even if no longer approved
            if (this.isEditing && this.target.vendor) {
                const currentVendorInList = this.vendorOptions.some(x => x.value === this.target.vendor)
                if (!currentVendorInList) {
                    const allVendor = this.parentComponent.vendors.find(x => x.id === this.target.vendor)
                    if (allVendor) {
                        this.vendorOptions.push(new ListItem(allVendor.id, allVendor.name))
                    }
                }
            }
        } else {
            this.vendorOptions = this.parentComponent.vendors.map(x => new ListItem(x.id, x.name))
        }
    }

    onWizardJobChanged() {
        const job = this.wizardSelectedJob
        if (!job || job === '') {
            return
        }

        this.target.job = job

        // Sync the parent's selected job so the toolbar dropdown and grid stay in sync
        this.parentComponent.selectedJob.set(job)
        this.parentComponent.onJobChanged()

        this.fetchVendorsForJob(job)
    }

    private fetchVendorsForJob(job: string) {
        this.isLoadingVendors = true

        // Fetch approved vendors for the selected job
        this.services.data
            .get('api/PurchaseOrder/GetApprovedVendorsByJob', new HttpParams().set('job', job))
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (result: any) => {
                    this.parentComponent.approvedVendors = result as any[]

                    // Populate vendor options from the freshly loaded approved vendors
                    if (result && result.length > 0) {
                        this.vendorOptions = result.map((x: any) => new ListItem(x.vendorId, x.vendorName))
                    } else {
                        this.vendorOptions = this.parentComponent.vendors.map(x => new ListItem(x.id, x.name))
                    }

                    this.isLoadingVendors = false
                    this.crd.detectChanges()
                },
                error: () => {
                    // Fall back to all vendors on error
                    this.vendorOptions = this.parentComponent.vendors.map(x => new ListItem(x.id, x.name))
                    this.isLoadingVendors = false
                    this.crd.detectChanges()
                },
            })
    }

    /**
     * Called by the parent component when the job list finishes loading,
     * in case the modal opened before the data was ready.
     */
    onJobListReady() {
        this.jobOptions = this.parentComponent.jobMap
        this.isJobListLoading = false
        this.crd.detectChanges()
    }

    selectType(type: PurchaseOrderType) {
        this.target.type = type
        if (type === PurchaseOrderType.Equipment) {
            this.step = 1
        } else {
            this.step = 2
        }
        this.updateModalSize()
        this.crd.detectChanges()
    }

    selectWorkOrder(isWorkOrder: boolean) {
        this.isWorkOrder = isWorkOrder
        this.step = 2
        this.updateModalSize()
        this.crd.detectChanges()
    }

    goBack() {
        if (this.step === 2) {
            if (this.target.type === PurchaseOrderType.Equipment) {
                this.step = 1
            } else {
                this.step = 0
                this.target.type = undefined as any
            }
        } else if (this.step === 1) {
            this.step = 0
            this.target.type = undefined as any
        }
        this.updateModalSize()
        this.crd.detectChanges()
    }

    viewRecords() {
        this.activeModal.close({ viewRecords: true })
    }

    removeImage(image) {
        this.images.remove(image)
    }

    getPendingImageSrc(image) {
        if (image.type == 'application/pdf') {
            return './Resources/pdfImage.png'
        }
        return image.url
    }

    getExistingImageSrc(url: string) {
        if (url.indexOf('.pdf') > -1) {
            return './Resources/pdfImage.png'
        }
        return './Resources/Images/PurchaseOrder/' + url + '?w=300'
    }

    isPdf(url: string): boolean {
        return url.toLowerCase().endsWith('.pdf')
    }

    openImage(url) {
        window.open('./Resources/Images/PurchaseOrder/' + url, '_blank')
    }

    async onFileChanged(event) {
        const files = event.target.files

        const progress = (p: number) => {
            this.progress = p
        }

        for (const file of files) {
            if (file.type !== 'application/pdf') {
                const options: Options = {
                    maxSizeMB: 2,
                    maxWidthOrHeight: 1920,
                    useWebWorker: true,
                    onProgress: progress,
                }
                const compressedFile = await imageCompression(file, options)
                compressedFile['url'] = await imageCompression.getDataUrlFromFile(compressedFile)
                this.images.push(compressedFile)
            } else {
                this.images.push(file)
            }
        }
    }

    protected onSave(resolveSave: (result?: any) => void, rejectSave: (reason?: any) => void) {
        const observable = this.isAdding || this.isDuplicating ? this.repositoryAdd() : this.repositoryEdit()
        observable.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
            next: successResult => {
                if (this.images.length === 0) {
                    resolveSave(successResult)
                    return
                }

                // If the saving of the record is successful, we will upload any images.
                const uploadData = new FormData()
                uploadData.append('id', successResult.id)
                for (let i = 0; i < this.images.length; i++) {
                    uploadData.append('myFile' + i, this.images[i], this.images[i].name)
                }
                this.services.data
                    .post('api/PurchaseOrder/UploadImages', uploadData)
                    .pipe(takeUntilDestroyed(this.destroyRef))
                    .subscribe({
                        next: _uploadResult => {
                            resolveSave(successResult)
                        },
                        error: errorResult => {
                            rejectSave(errorResult)
                        },
                    })
            },
            error: errorResult => {
                rejectSave(errorResult)
            },
        })
    }

    protected onBeforeSave() {
        if (!this.target.createdOn) {
            this.target.createdOn = new Date()
        }
        return true
    }
}
